import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, Lock, Eye, FileCheck, ChevronRight, Mail, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_LABEL = {
  signal_only: 'Signal only',
  pre_interview: 'Pre-interview',
  pre_offer: 'Pre-offer',
  post_acceptance: 'Post-acceptance',
};

const REGULATOR_TO_TEMPLATE = {
  'fca-uk': { id: 'banking_smf', name: 'FCA / PRA (UK) — Senior Manager Function' },
  'fca-cert': { id: 'fca_cert_regime', name: 'FCA (UK) — Certification Regime' },
  'finra-us': { id: 'us_financial_advisor', name: 'FINRA / SEC (US) — Financial Advisor' },
  'general': { id: 'standard', name: 'General — Standard hire' },
};

const PRINCIPLES = [
  {
    icon: Eye, title: 'Signal, not document',
    body: 'A "verified" badge tells the employer Vault Hire holds your document — without the document leaving your vault until you decide it should. Generic by design: never the document type, country, or expiry until release stage.',
  },
  {
    icon: Lock, title: 'Stage-locked release',
    body: 'Each item has a declared stage at which the actual content is shared (pre-offer / post-acceptance / never). Release rules are enforced server-side, not in the UI.',
  },
  {
    icon: FileCheck, title: 'Lawful basis per item',
    body: 'Every requirement an employer adds carries one of four UK GDPR Article 6 bases. Restricted items (credit check, DBS Standard/Enhanced, FINRA U4, fingerprint) are blocked unless the role is flagged with the matching regulator context.',
  },
  {
    icon: Shield, title: 'Ranking-blind by architecture',
    body: 'Pack readiness signals are deliberately isolated from our credibility-scoring, matching and AI surfaces. A "ready pack" never moves you up an algorithmic ranking. The mechanism that guarantees this is covered under our security briefing (NDA).',
  },
];

function PublicTopBar() {
  return (
    <header className="border-b border-[var(--vh-border)] bg-[var(--vh-bg-base)]/85 backdrop-blur sticky top-0 z-40">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl tracking-tight text-[var(--vh-brand-primary)]" data-testid="datamin-brand">
          <span className="inline-flex w-8 h-8 rounded-md bg-[var(--vh-brand-primary)] text-white items-center justify-center text-sm font-bold">V</span>
          Vault Hire
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[var(--vh-text-secondary)]">
          <Link to="/jobs" className="hover:text-[var(--vh-brand-primary)]">Jobs board</Link>
          <Link to="/companies" className="hover:text-[var(--vh-brand-primary)]">Companies</Link>
          <Link to="/trust" className="text-[var(--vh-brand-primary)] font-semibold">Trust</Link>
          <Link to="/pricing" className="hover:text-[var(--vh-brand-primary)]">Pricing</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-4 py-2 rounded-full text-sm text-[var(--vh-brand-primary)] hover:bg-[var(--vh-bg-muted)]">Sign in</Link>
          <Link to="/register" className="px-4 py-2 rounded-full text-sm bg-[var(--vh-brand-primary)] text-white hover:bg-[var(--vh-brand-primary-hover)]">Get started</Link>
        </div>
      </div>
    </header>
  );
}

function TemplateCard({ t, testId }) {
  return (
    <div className="rounded-2xl p-6 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]" data-testid={testId}>
      <h3 className="font-display text-lg text-[var(--vh-text-primary)] mb-1">{t.name}</h3>
      {t.regulator && <p className="text-xs text-[var(--vh-accent)] mb-2 uppercase tracking-[0.15em]">{t.regulator}</p>}
      <p className="text-xs text-[var(--vh-text-secondary)] mb-4 leading-relaxed">{t.description}</p>
      <ul className="space-y-2 text-xs">
        {t.items.map((i) => (
          <li key={i.category} className="flex items-center gap-2">
            <ChevronRight className="w-3 h-3 text-[var(--vh-accent)] shrink-0" />
            <span className="font-mono text-[var(--vh-text-primary)]">{i.category}</span>
            <span className="text-[var(--vh-text-muted)]">·</span>
            <span className="text-[var(--vh-text-secondary)]">{STAGE_LABEL[i.release_stage] || i.release_stage}</span>
            {i.lookback_years && <span className="text-[var(--vh-text-muted)] text-[10px] ml-auto">{i.lookback_years}-yr lookback</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function TrustDataMinimisationPage() {
  const [tpl, setTpl] = useState([]);
  const [tplMap, setTplMap] = useState({});
  const [regChoice, setRegChoice] = useState('fca-uk');
  const [form, setForm] = useState({ name: '', email: '', org: '', role_focus: 'fca-uk' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/manifest/templates`).then((r) => {
      setTpl(r.data.templates || []);
      const m = {};
      (r.data.templates || []).forEach((t) => { m[t.id] = t; });
      setTplMap(m);
    }).catch(() => {});
  }, []);

  const currentTplId = REGULATOR_TO_TEMPLATE[regChoice]?.id;
  const currentTpl = tplMap[currentTplId];

  const submitLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/dpia-requests`, { ...form, regulator: regChoice });
      setSubmitted(true);
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Could not submit — please email trust@vault-hire.com');
    }
  };

  return (
    <div className="min-h-screen bg-[var(--vh-bg-base)] text-[var(--vh-text-primary)]" data-testid="trust-data-min-page">
      <PublicTopBar />

      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10">
        <Link to="/trust" className="text-xs text-[var(--vh-text-muted)] hover:text-[var(--vh-brand-primary)] inline-flex items-center gap-1 mb-5">← Back to Trust</Link>
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-3">Privacy by design</p>
        <h1 className="font-display text-4xl md:text-5xl leading-[1.06] mb-5" data-testid="trust-data-min-title">
          You decide what crosses the wire — and when.
        </h1>
        <p className="text-lg text-[var(--vh-text-secondary)] leading-relaxed">
          Most ATSs collect candidate documents on day one and forget to delete them on day 200. Vault Hire does the opposite: <strong className="text-[var(--vh-text-primary)]">nothing leaves your vault until the moment it's legally required</strong>. Employers see a <em>signal</em> that you have what they need — not the document itself — until you both reach the stage where release is justified.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          {PRINCIPLES.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="rounded-2xl p-6 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]" data-testid={`principle-${p.title.toLowerCase().replace(/\W+/g, '-')}`}>
                <div className="flex items-center gap-2 mb-3 text-[var(--vh-brand-primary)]">
                  <span className="w-9 h-9 rounded-xl bg-[var(--vh-bg-muted)] flex items-center justify-center">
                    <Icon className="w-4 h-4" />
                  </span>
                  <h3 className="font-display text-lg text-[var(--vh-text-primary)]">{p.title}</h3>
                </div>
                <p className="text-sm text-[var(--vh-text-secondary)] leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <h2 className="font-display text-2xl md:text-3xl text-[var(--vh-text-primary)] mb-3">How it feels for both sides</h2>
        <p className="text-[var(--vh-text-secondary)] text-sm mb-6 max-w-3xl leading-relaxed">A high-level view of the flow. The step-by-step mechanics — including the enforcement layer that makes early-stage release impossible even for employer admins — are covered in our security briefing under NDA.</p>
        <ol className="space-y-3 text-sm text-[var(--vh-text-secondary)]" data-testid="flow-steps">
          {[
            ['Employer publishes a manifest.', 'When the job is created, they pick exactly which items they need and declare a lawful basis + release stage for each. Restricted items are blocked unless the role is genuinely regulated.'],
            ['Candidate sees the checklist.', 'On the application, you see exactly what\'s needed and what you already hold. For each item you have, you decide whether to switch the "signal" ON.'],
            ['Employer sees coarse signals.', '"Pack 4/7 ready" — never the document. Every view by the employer is logged in your candidate audit log.'],
            ['Release happens at the declared stage.', 'Early-stage access to a later-stage document is architecturally impossible — including for employer admins.'],
            ['Revoke any time.', 'Withdraw the application and the signals + audit linkage are tombstoned in line with our retention policy.'],
          ].map(([head, body], idx) => (
            <li key={head} className="flex gap-4 p-4 bg-[var(--vh-bg-surface)] rounded-2xl border border-[var(--vh-border)]">
              <span className="font-mono text-xs text-[var(--vh-accent)] shrink-0 pt-0.5">{String(idx + 1).padStart(2, '0')}</span>
              <span><strong className="text-[var(--vh-text-primary)]">{head}</strong> {body}</span>
            </li>
          ))}
        </ol>
      </section>

      {tpl.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 pb-16">
          <h2 className="font-display text-2xl md:text-3xl text-[var(--vh-text-primary)] mb-2">What a real pack looks like</h2>
          <p className="text-[var(--vh-text-secondary)] text-sm mb-6">Examples from our public template catalogue (employers can edit or write their own).</p>
          <div className="grid md:grid-cols-2 gap-5">
            {tpl.map((t) => (<TemplateCard key={t.id} t={t} testId={`template-${t.id}`} />))}
          </div>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-6 pb-16">
        <h2 className="font-display text-2xl md:text-3xl text-[var(--vh-text-primary)] mb-4">Regulatory reference points</h2>
        <ul className="text-sm text-[var(--vh-text-secondary)] space-y-2.5 leading-relaxed">
          <li><span className="text-[var(--vh-accent)] font-mono mr-2">§</span><strong className="text-[var(--vh-text-primary)]">UK GDPR Art. 5(1)(c)</strong> — data minimisation. Only the minimum personal data necessary for the specified purpose.</li>
          <li><span className="text-[var(--vh-accent)] font-mono mr-2">§</span><strong className="text-[var(--vh-text-primary)]">UK GDPR Art. 25</strong> — data protection by design and by default. Default settings limit what is processed.</li>
          <li><span className="text-[var(--vh-accent)] font-mono mr-2">§</span><strong className="text-[var(--vh-text-primary)]">UK GDPR Art. 7(4)</strong> — consent must be freely given. Our UI never penalises candidates who don't toggle signals on.</li>
          <li><span className="text-[var(--vh-accent)] font-mono mr-2">§</span><strong className="text-[var(--vh-text-primary)]">UK Equality Act 2010</strong> — restricted checks (credit, DBS Std/Enh) gated by role flag to prevent indirect discrimination.</li>
          <li><span className="text-[var(--vh-accent)] font-mono mr-2">§</span><strong className="text-[var(--vh-text-primary)]">FCA SYSC 22</strong> — regulatory references for SMF moves with 6-year lookback handled as a first-class item.</li>
          <li><span className="text-[var(--vh-accent)] font-mono mr-2">§</span><strong className="text-[var(--vh-text-primary)]">EU AI Act Art. 6 / Annex III</strong> — pack readiness signals are deliberately kept outside the data fed to any ranking model. Architectural detail covered under NDA.</li>
        </ul>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16" data-testid="regulator-picker-section">
        <div className="rounded-3xl p-7 md:p-9 bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]">
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--vh-accent)] mb-2">Pack preview</p>
          <h2 className="font-display text-2xl md:text-3xl text-[var(--vh-text-primary)] mb-2">Pick your regulator — see exactly what we'd collect</h2>
          <p className="text-[var(--vh-text-secondary)] text-sm mb-6 max-w-2xl">Every line below is gated by the candidate's explicit signal toggle. The pack updates live as you switch regulators.</p>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-semibold mb-2 text-[var(--vh-text-secondary)] uppercase tracking-[0.12em]">Choose your regulatory context</label>
              <select
                value={regChoice}
                onChange={(e) => { setRegChoice(e.target.value); setForm((f) => ({ ...f, role_focus: e.target.value })); }}
                data-testid="trust-regulator-select"
                className="w-full px-3 py-2.5 rounded-lg bg-[var(--vh-bg-base)] border border-[var(--vh-border)] text-sm text-[var(--vh-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]"
              >
                {Object.entries(REGULATOR_TO_TEMPLATE).map(([k, v]) => (
                  <option key={k} value={k}>{v.name}</option>
                ))}
              </select>
              {currentTpl && (
                <div className="mt-4 p-4 rounded-2xl bg-[var(--vh-bg-muted)] border border-[var(--vh-border)]" data-testid="trust-pack-preview">
                  <p className="text-xs uppercase tracking-[0.15em] text-[var(--vh-accent)] mb-2">{currentTpl.regulator || '—'}</p>
                  <p className="text-xs text-[var(--vh-text-secondary)] mb-3 leading-relaxed">{currentTpl.description}</p>
                  <ul className="space-y-1.5 text-xs">
                    {currentTpl.items.map((i) => (
                      <li key={i.category} className="flex items-center gap-2">
                        <ChevronRight className="w-3 h-3 text-[var(--vh-accent)] shrink-0" />
                        <span className="font-mono text-[var(--vh-text-primary)]">{i.category}</span>
                        <span className="text-[var(--vh-text-muted)]">·</span>
                        <span className="text-[var(--vh-text-secondary)]">{STAGE_LABEL[i.release_stage] || i.release_stage}</span>
                        {i.lookback_years && <span className="text-[var(--vh-text-muted)] text-[10px] ml-auto">{i.lookback_years}-yr lookback</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold mb-2 text-[var(--vh-text-secondary)] uppercase tracking-[0.12em]">Request a security briefing for this pack</label>
              {submitted ? (
                <div className="rounded-2xl p-5 bg-[var(--vh-bg-muted)] border border-[var(--vh-border)] text-sm flex items-start gap-3" data-testid="trust-dpia-submitted">
                  <CheckCircle2 className="w-5 h-5 shrink-0 text-[var(--vh-accent)]" />
                  <div>
                    <p className="font-semibold mb-1 text-[var(--vh-text-primary)]">Request received — under review.</p>
                    <p className="text-xs text-[var(--vh-text-secondary)] leading-relaxed">A Vault Hire compliance officer will verify your details and reach out within 1 working day with a personalised security briefing and (where appropriate) portal access.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={submitLead} className="space-y-2.5" data-testid="trust-dpia-form">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="trust-dpia-name" className="w-full px-3 py-2.5 rounded-lg bg-[var(--vh-bg-base)] border border-[var(--vh-border)] text-sm placeholder-[var(--vh-text-muted)] text-[var(--vh-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]" />
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Work email (no free/personal addresses)" data-testid="trust-dpia-email" className="w-full px-3 py-2.5 rounded-lg bg-[var(--vh-bg-base)] border border-[var(--vh-border)] text-sm placeholder-[var(--vh-text-muted)] text-[var(--vh-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]" />
                  <input required value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} placeholder="Organisation" data-testid="trust-dpia-org" className="w-full px-3 py-2.5 rounded-lg bg-[var(--vh-bg-base)] border border-[var(--vh-border)] text-sm placeholder-[var(--vh-text-muted)] text-[var(--vh-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--vh-accent)]" />
                  <button type="submit" data-testid="trust-dpia-submit" className="w-full px-4 py-2.5 rounded-full bg-[var(--vh-accent)] hover:bg-[var(--vh-accent-hover)] text-white font-semibold text-sm inline-flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4" /> Request security briefing
                  </button>
                  <p className="text-[10px] text-[var(--vh-text-muted)] leading-tight">Requests are manually vetted by a Vault Hire compliance officer — we verify identity and work email before sharing anything sensitive. No auto-delivery, no marketing.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
        <h2 className="font-display text-2xl md:text-3xl text-[var(--vh-text-primary)] mb-3">Already in a vendor review?</h2>
        <p className="text-[var(--vh-text-secondary)] mb-6 leading-relaxed">If your compliance team has a security questionnaire, ask us for portal access — you can submit questions one-by-one and we (or our AI, with full source citations) will answer.</p>
        <a
          href="mailto:trust@vault-hire.com?subject=Compliance%20Portal%20Access"
          data-testid="dpia-request-btn"
          className="inline-block px-6 py-3 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-sm font-semibold"
        >
          Request portal access
        </a>
      </section>

      <footer className="border-t border-[var(--vh-border)] bg-[var(--vh-bg-muted)] py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs text-[var(--vh-text-muted)]">
          <Link to="/trust" className="underline hover:text-[var(--vh-brand-primary)]">← Back to Trust</Link>
        </div>
      </footer>
    </div>
  );
}
